import { useEffect, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

import Modal from "components/Modal";

import type { OfferingMedia } from "./types";
import { getOfferingMediaKind, getOfferingMediaLabel } from "./offeringMedia";

type OfferingMediaGalleryModalProps = {
  // Pre-sorted by the parent (getSortedOfferingMedia).
  media: OfferingMedia[];
  serviceName: string;
  isOpen: boolean;
  onClose: () => void;
};

function getMediaKey(media: OfferingMedia) {
  return media.id || media.url;
}

export default function OfferingMediaGalleryModal({
  media,
  serviceName,
  isOpen,
  onClose,
}: OfferingMediaGalleryModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [failedMedia, setFailedMedia] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(0);
    setFailedMedia({});
  }, [isOpen]);

  if (!media.length) return null;

  const activeMedia = media[activeIndex] ?? media[0];
  const activeKind = getOfferingMediaKind(activeMedia);
  const activeKey = getMediaKey(activeMedia);
  const activeLabel = getOfferingMediaLabel(
    activeMedia,
    serviceName,
    activeIndex,
  );
  const previewFailed = failedMedia[activeKey];
  const hasMultipleMedia = media.length > 1;

  const markFailed = (mediaItem: OfferingMedia) => {
    setFailedMedia((prev) => ({ ...prev, [getMediaKey(mediaItem)]: true }));
  };

  const goToOffset = (offset: number) => {
    setActiveIndex((prev) => {
      const nextIndex = prev + offset;
      if (nextIndex < 0) return media.length - 1;
      if (nextIndex >= media.length) return 0;
      return nextIndex;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={serviceName}
      maxWidthClassName="max-w-2xl"
    >
      <div className="flex flex-col gap-3" data-testid="offering-media-gallery">
        <div className="flex items-center justify-center overflow-hidden rounded-md bg-ivory">
          {activeKind === "video" && !previewFailed && (
            <video
              key={activeMedia.url}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="max-h-[75vh] w-full rounded-md bg-black object-contain"
              onError={() => markFailed(activeMedia)}
              data-testid="offering-media-video"
            >
              <source src={activeMedia.url} type={activeMedia.mime_type} />
            </video>
          )}

          {activeKind === "image" && !previewFailed && (
            <img
              src={activeMedia.url}
              alt={activeLabel}
              className="max-h-[60vh] w-full object-contain"
              onError={() => markFailed(activeMedia)}
              data-testid="offering-media-image"
            />
          )}

          {previewFailed && (
            <div
              className="flex min-h-48 w-full flex-col items-center justify-center p-6 text-center"
              data-testid="offering-media-fallback"
            >
              <p className="text-lg font-semibold text-evergreen">
                Preview unavailable
              </p>
              <a
                href={activeMedia.url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-evergreen px-4 py-2 text-sm font-semibold text-evergreen transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-evergreen focus:ring-offset-2"
              >
                Open media
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>

        {hasMultipleMedia && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => goToOffset(-1)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-oat text-evergreen transition hover:bg-ivory focus:outline-none focus:ring-2 focus:ring-evergreen focus:ring-offset-2"
              aria-label="Show previous media"
              data-testid="offering-media-gallery-prev"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <p className="text-sm font-medium text-soil">
              {activeIndex + 1} of {media.length}
            </p>
            <button
              type="button"
              onClick={() => goToOffset(1)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-oat text-evergreen transition hover:bg-ivory focus:outline-none focus:ring-2 focus:ring-evergreen focus:ring-offset-2"
              aria-label="Show next media"
              data-testid="offering-media-gallery-next"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
